import { ApiError } from './errors.js'
import { decodeStream, parseNdjsonStream } from './ndjson.js'

/**
 * @typedef {object} TokenStore
 * @property {() => (string|null)} getAccessToken 当前 accessToken；匿名/未登录返回 null
 * @property {() => (string|null)} getRefreshToken 当前 refreshToken；未登录返回 null
 * @property {() => string} getDeviceId 稳定设备标识（x-device-id）
 * @property {() => Promise<string>} refresh 轮转刷新令牌，返回新的 accessToken；失败抛错
 */

/**
 * @typedef {object} TransportOpts
 * @property {string} baseUrl
 * @property {TokenStore} tokenStore
 * @property {(() => void)} [onAuthExpired]
 * @property {(() => string)} [getLocale]
 * @property {string} [deviceId]
 * @property {typeof fetch} fetchImpl
 */

/**
 * @typedef {object} RequestOpts
 * @property {boolean} [public] 公开端点：不带 Authorization，401 不触发刷新
 * @property {object} [query] 拼到 URL 上的查询参数
 * @property {any} [body] JSON 请求体
 * @property {(input:any)=>any} [parse] 对成功响应体跑 contracts 校验
 * @property {number} [timeoutMs] 请求超时毫秒数；超时后 Abort 并交由调用方失败路径处理
 */

/**
 * 创建核心请求层：唯一拼 header / 发 fetch / 做 401 单航班刷新+重放 / 包 ApiError 的地方。
 * 域模块只调它的 `request` / `stream`，不重复任何横切逻辑。
 *
 * @param {TransportOpts} opts
 */
export function createTransport(opts) {
  const { baseUrl, tokenStore, onAuthExpired, getLocale, deviceId, fetchImpl } = opts

  /** 单航班刷新锁：并发 401 共享同一次 refresh()。 @type {Promise<string>|null} */
  let inflightRefresh = null

  /** @returns {Promise<string>} */
  function refreshOnce() {
    if (!inflightRefresh) {
      inflightRefresh = Promise.resolve()
        .then(() => tokenStore.refresh())
        .finally(() => {
          inflightRefresh = null
        })
    }
    return inflightRefresh
  }

  /**
   * @param {RequestOpts & { isPublic:boolean }} cfg
   * @returns {Record<string,string>}
   */
  function buildHeaders(cfg) {
    /** @type {Record<string,string>} */
    const headers = {
      'x-device-id': deviceId ?? tokenStore.getDeviceId(),
      'x-locale': getLocale ? getLocale() : 'en',
    }
    if (cfg.body !== undefined) headers['content-type'] = 'application/json'
    if (!cfg.isPublic) {
      const token = tokenStore.getAccessToken()
      if (token) headers['authorization'] = `Bearer ${token}`
    }
    return headers
  }

  /**
   * @param {string} path
   * @param {object} [query]
   * @returns {string}
   */
  function buildUrl(path, query) {
    // baseUrl 为空或纯根 '/' = 同源相对路径：本地走 vite proxy、线上走 nginx proxy，
    // 前端永远发 `/api/...` 相对路径，浏览器按当前 origin 解析，无需 CORS、无需注入域名。
    // （'/' 不是合法的 URL() 绝对 base，会抛 "Invalid base URL"，故与空串同等处理。）
    if (!baseUrl || baseUrl === '/') {
      if (!query) return path
      const qs = new URLSearchParams()
      for (const [k, val] of Object.entries(query)) {
        if (val === undefined || val === null) continue
        qs.set(k, String(val))
      }
      const s = qs.toString()
      return s ? `${path}${path.includes('?') ? '&' : '?'}${s}` : path
    }
    const url = new URL(path, ensureTrailingBase(baseUrl))
    if (query) {
      for (const [k, val] of Object.entries(query)) {
        if (val === undefined || val === null) continue
        url.searchParams.set(k, String(val))
      }
    }
    return url.toString()
  }

  /**
   * 发一次原始 fetch（不含重试逻辑）。
   * @param {string} method
   * @param {string} path
   * @param {RequestOpts} ro
   * @param {boolean} stream 流式请求带 ndjson Accept
   * @returns {Promise<Response>}
   */
  function rawFetch(method, path, ro, stream) {
    const isPublic = ro.public === true
    const headers = buildHeaders({ ...ro, isPublic })
    if (stream) headers['accept'] = 'application/x-ndjson'
    return fetchWithTimeout(fetchImpl, buildUrl(path, ro.query), {
      method,
      headers,
      body: ro.body !== undefined ? JSON.stringify(ro.body) : undefined,
    }, ro.timeoutMs)
  }

  /**
   * 收到 401（且非公开请求）时：刷新一次后重放原请求；二次仍 401 → onAuthExpired + 抛。
   * @param {() => Promise<Response>} send
   * @param {boolean} isPublic
   * @returns {Promise<Response>}
   */
  async function withRefresh(send, isPublic) {
    let res = await send()
    if (res.status !== 401 || isPublic) return res
    try {
      await refreshOnce()
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) onAuthExpired?.()
      throw error
    }
    res = await send()
    if (res.status === 401) {
      onAuthExpired?.()
      throw await toApiError(res)
    }
    return res
  }

  /**
   * JSON 请求：成功返解析后的 body（可选 contracts 校验），失败抛 ApiError。
   * @param {string} method
   * @param {string} path
   * @param {RequestOpts} [ro]
   * @returns {Promise<any>}
   */
  async function request(method, path, ro = {}) {
    const res = await withRefresh(() => rawFetch(method, path, ro, false), ro.public === true)
    if (!res.ok) throw await toApiError(res)
    const data = res.status === 204 ? undefined : await res.json()
    return ro.parse ? ro.parse(data) : data
  }

  /**
   * 文本请求：用于 CSV 等非 JSON 下载。
   * @param {string} method
   * @param {string} path
   * @param {RequestOpts} [ro]
   * @returns {Promise<string>}
   */
  async function text(method, path, ro = {}) {
    const res = await withRefresh(() => rawFetch(method, path, ro, false), ro.public === true)
    if (!res.ok) throw await toApiError(res)
    return res.text()
  }

  /**
   * 二进制请求：用于需要鉴权的文件下载。
   * @param {string} method
   * @param {string} path
   * @param {RequestOpts} [ro]
   */
  async function binary(method, path, ro = {}) {
    const res = await withRefresh(() => rawFetch(method, path, ro, false), ro.public === true)
    if (!res.ok) throw await toApiError(res)
    const contentType = res.headers.get('content-type')
    if (contentType === null || contentType.length === 0) throw new Error(`Binary response content-type is missing: ${path}`)
    return {
      bytes: await res.arrayBuffer(),
      content_type: contentType,
    }
  }

  /**
   * 流式 NDJSON 请求：返回 async iterable，逐行 yield（可选 contracts 校验）。
   * @param {string} method
   * @param {string} path
   * @param {RequestOpts} [ro]
   * @returns {AsyncGenerator<any, void, void>}
   */
  async function* stream(method, path, ro = {}) {
    const res = await withRefresh(() => rawFetch(method, path, ro, true), ro.public === true)
    if (!res.ok) throw await toApiError(res)
    if (!res.body) return
    for await (const obj of parseNdjsonStream(decodeStream(res.body))) {
      yield ro.parse ? ro.parse(obj) : obj
    }
  }

  function openWebSocket(path, { start, onMessage, onClose }) {
    if (typeof globalThis.WebSocket !== 'function') throw new Error('WebSocket is unavailable')
    const socket = new globalThis.WebSocket(webSocketUrl(baseUrl, path))
    socket.binaryType = 'arraybuffer'
    const ready = new Promise((resolve, reject) => {
      let opened = false
      let initialized = false
      socket.addEventListener('open', () => {
        opened = true
        socket.send(JSON.stringify({
          ...start,
          access_token: tokenStore.getAccessToken(),
          device_id: deviceId ?? tokenStore.getDeviceId(),
        }))
      })
      socket.addEventListener('message', event => {
        const message = JSON.parse(String(event.data))
        if (message.type === 'ready') {
          initialized = true
          resolve()
        }
        if (message.type === 'error' && !initialized) reject(new Error(message.message))
        onMessage(message)
      })
      socket.addEventListener('error', () => {
        if (!opened) reject(new Error('WebSocket connection failed'))
      })
      socket.addEventListener('close', event => {
        if (!initialized) reject(new Error(`WebSocket closed before ASR initialization: ${event.code}`))
        onClose(event)
      })
    })
    return {
      ready,
      sendBinary(bytes) {
        if (socket.readyState !== globalThis.WebSocket.OPEN) throw new Error('WebSocket is not open')
        socket.send(bytes)
      },
      sendJson(message) {
        if (socket.readyState !== globalThis.WebSocket.OPEN) throw new Error('WebSocket is not open')
        socket.send(JSON.stringify(message))
      },
      close() {
        socket.close()
      },
    }
  }

  return { binary, openWebSocket, request, stream, text }
}

function webSocketUrl(baseUrl, path) {
  const url = !baseUrl || baseUrl === '/'
    ? new URL(path, globalThis.location.origin)
    : new URL(path, ensureTrailingBase(baseUrl))
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return url.toString()
}

function fetchWithTimeout(fetchImpl, fetchUrl, init, timeoutMs) {
  const ms = Number(timeoutMs || 0)
  if (!Number.isFinite(ms) || ms <= 0) return fetchImpl(fetchUrl, init)
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ms)
  return fetchImpl(fetchUrl, { ...init, signal: controller.signal })
    .finally(() => clearTimeout(timer))
}

/**
 * 把非 2xx 响应解包成 ApiError。优先读 contracts 的 `{ error:{...} }` 形状，
 * 拿不到则回退到 HTTP 状态推断，永不返回 undefined、永不吞错。
 * @param {Response} res
 * @returns {Promise<ApiError>}
 */
async function toApiError(res) {
  const requestId = res.headers.get('x-request-id') ?? undefined
  let code = `http_${res.status}`
  let message = res.statusText || `HTTP ${res.status}`
  let retryable = res.status >= 500 || res.status === 429
  let details
  try {
    const payload = /** @type {any} */ (await res.json())
    const err = payload && payload.error
    if (err && typeof err === 'object') {
      if (typeof err.code === 'string') code = err.code
      if (typeof err.message === 'string') message = err.message
      if (typeof err.retryable === 'boolean') retryable = err.retryable
      if (err.details && typeof err.details === 'object') details = err.details
    }
  } catch {
    // 响应无 JSON body：保留上面的 HTTP 推断值。
  }
  return new ApiError({ status: res.status, code, message, requestId, retryable, details })
}

/**
 * @param {string} base
 * @returns {string}
 */
function ensureTrailingBase(base) {
  return base.endsWith('/') ? base : base + '/'
}
